import { useState, useEffect, useRef, useCallback } from "react";
import { TableHead } from "./table";

/**
 * Hook to manage resizable column widths, persisted in localStorage.
 *
 * @param {string} storageKey - Unique key for this table (e.g. "leads-table")
 * @param {Record<string, number>} defaults - Default widths per column id
 * @returns {{ widths, setWidth, getStyle }}
 */
export function useResizableColumns(storageKey, defaults = {}) {
  const [widths, setWidths] = useState(() => {
    try {
      const stored = localStorage.getItem(`rt:${storageKey}`);
      if (stored) return { ...defaults, ...JSON.parse(stored) };
    } catch {}
    return defaults;
  });

  const persist = useCallback((next) => {
    setWidths(next);
    try {
      localStorage.setItem(`rt:${storageKey}`, JSON.stringify(next));
    } catch {}
  }, [storageKey]);

  const setWidth = useCallback((id, width) => {
    persist({ ...widths, [id]: width });
  }, [persist, widths]);

  const getStyle = useCallback((id) => {
    const w = widths[id];
    return w ? { width: `${w}px`, minWidth: `${w}px`, maxWidth: `${w}px` } : undefined;
  }, [widths]);

  return { widths, setWidth, getStyle };
}

/**
 * TableHead with a drag handle on the right edge to resize the column.
 * Also updates the width of every cell in the same column index.
 *
 * @param {{ id: string, width?: number, onWidthChange: (w: number) => void, minWidth?: number }} props
 */
export function ResizableTableHead({ id, width, onWidthChange, minWidth = 80, className = "", children, ...rest }) {
  const thRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const th = thRef.current;
    if (!th) return;

    const startX = e.clientX;
    const startWidth = th.getBoundingClientRect().width;
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      const next = Math.max(minWidth, Math.round(startWidth + delta));
      onWidthChange(next);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizing(false);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleDoubleClick = () => {
    // Reset to auto width
    onWidthChange(null);
  };

  const style = width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : undefined;

  return (
    <TableHead
      ref={thRef}
      className={`relative group select-none ${className}`}
      style={style}
      {...rest}
    >
      <span className="block truncate pr-2" title={typeof children === "string" ? children : undefined}>
        {children}
      </span>
      <span
        role="separator"
        aria-label="Redimensionner la colonne"
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        data-testid={`resize-handle-${id}`}
        className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize flex items-center justify-center transition-colors ${
          isResizing ? "bg-[#C46A4A]" : "bg-transparent hover:bg-[#C46A4A]/40"
        }`}
      >
        <span className={`w-px h-1/2 bg-[#BFBFBF]/60 group-hover:bg-[#C46A4A] transition-colors ${isResizing ? "bg-[#C46A4A]" : ""}`} />
      </span>
    </TableHead>
  );
}
